import { type DataTableColumn, DataTableV2 } from "@/components/ui/data-table-v2";
import type { ColumnDef } from "@tanstack/react-table";
import type { LeaderboardResponse } from "./queries";

interface LeaderboardProps {
  data: LeaderboardResponse;
}

type DetectorRow = { detector: string; n_flagged: number; agreement: number; id?: string };
type EntryRow = {
  name: string;
  score_val: number | null;
  fit_time_sec?: number | null;
  pred_time_seconds?: number | null;
  id?: string;
};

export function Leaderboard({ data }: LeaderboardProps) {
  if (data.detectors && data.detectors.length > 0) {
    // Anomaly family: show detector table
    const columns: (ColumnDef<DetectorRow, unknown> & DataTableColumn)[] = [
      {
        accessorKey: "detector",
        header: "Detector",
        cell: (info) => <span className="font-medium text-ink">{info.getValue() as string}</span>,
      },
      {
        accessorKey: "n_flagged",
        header: "Flagged",
        cell: (info) => <span className="text-ink">{info.getValue() as number}</span>,
        mono: true,
      },
      {
        accessorKey: "agreement",
        header: "Agreement",
        cell: (info) => <span className="text-ink">{(info.getValue() as number).toFixed(3)}</span>,
        mono: true,
      },
    ];

    return (
      <DataTableV2
        data={data.detectors as DetectorRow[]}
        columns={columns}
        caption={`Anomaly Detectors (${data.detectors.length})`}
        sortLabel=""
      />
    );
  }

  if (data.entries && data.entries.length > 0) {
    // Tabular/Forecast family: show model leaderboard
    const sorted = [...data.entries].sort((a, b) => {
      // Sort by score_val desc, nulls at bottom
      if (a.score_val === null && b.score_val === null) return 0;
      if (a.score_val === null) return 1;
      if (b.score_val === null) return -1;
      return b.score_val - a.score_val;
    });

    const columns: (ColumnDef<EntryRow, unknown> & DataTableColumn)[] = [
      {
        accessorKey: "name",
        header: "Model",
        cell: (info) => <span className="font-medium text-ink">{info.getValue() as string}</span>,
      },
      {
        accessorKey: "score_val",
        header: data.metric_name ?? data.eval_metric ?? "Score",
        cell: (info) => {
          const val = info.getValue() as number | null;
          return <span className="text-ink">{val === null ? "—" : val.toFixed(4)}</span>;
        },
        mono: true,
      },
      {
        accessorKey: "fit_time_sec",
        header: "Fit Time (s)",
        cell: (info) => {
          const val = info.getValue() as number | null | undefined;
          return <span className="text-ink">{val == null ? "—" : val.toFixed(2)}</span>;
        },
        mono: true,
      },
      {
        accessorKey: "pred_time_seconds",
        header: "Pred Time (s)",
        cell: (info) => {
          const val = info.getValue() as number | null | undefined;
          return <span className="text-ink">{val == null ? "—" : val.toFixed(4)}</span>;
        },
        mono: true,
      },
    ];

    return (
      <DataTableV2
        data={sorted as EntryRow[]}
        columns={columns}
        caption={`Leaderboard (${sorted.length})`}
        sortLabel={
          data.metric_name || data.eval_metric
            ? `sorted · ${data.metric_name || data.eval_metric} desc`
            : "sorted · score desc"
        }
      />
    );
  }

  return (
    <div className="rounded-[14px] border border-line bg-surface p-5 text-center text-sm text-muted">
      No leaderboard data available
    </div>
  );
}
